import React from "react";
import {
  Button,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerHeaderTitle,
  OverlayDrawer,
  Text,
  makeStyles,
  tokens,
  useId,
} from "@fluentui/react-components";
import { Dismiss20Regular } from "@fluentui/react-icons";
import type { AnalyticsSettings, MetricType } from "../types";
import { DataFilters } from "./DataFilters";
import { MetricSelector } from "./MetricSelector";

const useStyles = makeStyles({
  drawer: {
    margin: "12px",
    borderRadius: tokens.borderRadiusXLarge,
    width: "400px",
    maxWidth: "100vw",
    "@media (max-width: 639px)": {
      width: "calc(100vw - 24px)  ",
    },
  },
  header: {
    flexShrink: 0,
  },
  body: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXL,
    minHeight: 0,
    overflowY: "auto",
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    marginBottom: tokens.spacingVerticalXL,
    marginTop: tokens.spacingVerticalXL,
    flexShrink: 0,
    minWidth: 0,
  },
  sectionTitle: {
    margin: 0,
  },
  validation: {
    color: tokens.colorPaletteRedForeground1,
  },
  footer: {
    flexShrink: 0,
    justifyContent: "flex-end",
    gap: tokens.spacingHorizontalS,
  },
});

interface AnalyticsSettingsDrawerProps {
  open: boolean;
  settings: AnalyticsSettings;
  appliedSettings: AnalyticsSettings;
  onChange: (settings: AnalyticsSettings) => void;
  onApply: (settings: AnalyticsSettings) => void;
  onDismiss: () => void;
  accessibleMetrics: MetricType[];
  countries: string[];
  countriesLoading: boolean;
  salesPeople: string[];
  salesPeopleLoading: boolean;
}

export const AnalyticsSettingsDrawer: React.FC<
  AnalyticsSettingsDrawerProps
> = ({
  open,
  settings,
  appliedSettings,
  onChange,
  onApply,
  onDismiss,
  accessibleMetrics,
  countries,
  countriesLoading,
  salesPeople,
  salesPeopleLoading,
}) => {
  const styles = useStyles();
  const titleId = useId("analytics-settings-title");
  const metricsId = useId("analytics-metrics");
  const filtersId = useId("analytics-filters");
  const validMetrics =
    settings.selectedMetrics.length >= 1 &&
    settings.selectedMetrics.length <= 3 &&
    settings.selectedMetrics.every((metric) =>
      accessibleMetrics.includes(metric),
    );
  const hasChanges =
    JSON.stringify(settings) !== JSON.stringify(appliedSettings);

  const updateSetting = <Key extends keyof AnalyticsSettings>(
    key: Key,
    value: AnalyticsSettings[Key],
  ) => onChange({ ...settings, [key]: value });

  return (
    <OverlayDrawer
      open={open}
      position="end"
      modalType="non-modal"
      aria-labelledby={titleId}
      className={styles.drawer}
      onOpenChange={(_, data) => {
        if (!data.open) onDismiss();
      }}
    >
      <DrawerHeader className={styles.header}>
        <DrawerHeaderTitle
          heading={{ id: titleId }}
          action={
            <Button
              appearance="subtle"
              icon={<Dismiss20Regular />}
              aria-label="Close metrics and filters"
              title="Close"
              onClick={onDismiss}
            />
          }
        >
          Options
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody className={styles.body}>
        <section className={styles.section} aria-labelledby={metricsId}>
          <Text
            as="h3"
            id={metricsId}
            weight="semibold"
            size={400}
            className={styles.sectionTitle}
          >
            Metrics
          </Text>
          <MetricSelector
            selectedMetrics={settings.selectedMetrics}
            onChange={(value) => updateSetting("selectedMetrics", value)}
            accessibleMetrics={accessibleMetrics}
          />
          {!validMetrics && (
            <Text className={styles.validation} role="status" size={200}>
              Select between 1 and 3 permitted metrics.
            </Text>
          )}
        </section>
        <section className={styles.section} aria-labelledby={filtersId}>
          <Text
            as="h3"
            id={filtersId}
            weight="semibold"
            size={400}
            className={styles.sectionTitle}
          >
            Filters
          </Text>
          <DataFilters
            liquidationFilter={settings.liquidationFilter}
            onLiquidationFilterChange={(value) =>
              updateSetting("liquidationFilter", value)
            }
            exceptionFilter={settings.exceptionFilter}
            onExceptionFilterChange={(value) =>
              updateSetting("exceptionFilter", value)
            }
            timePeriod={settings.timePeriod}
            onTimePeriodChange={(value) => updateSetting("timePeriod", value)}
            countries={countries}
            countriesLoading={countriesLoading}
            selectedCountries={settings.selectedCountries}
            onSelectedCountriesChange={(value) =>
              updateSetting("selectedCountries", value)
            }
            salesPeople={salesPeople}
            salesPeopleLoading={salesPeopleLoading}
            selectedSalesPeople={settings.selectedSalesPeople}
            onSelectedSalesPeopleChange={(value) =>
              updateSetting("selectedSalesPeople", value)
            }
          />
        </section>
      </DrawerBody>
      <DrawerFooter className={styles.footer}>
        <Button
          appearance="primary"
          disabled={!validMetrics || !hasChanges}
          onClick={() => {
            if (validMetrics && hasChanges) onApply(settings);
          }}
        >
          Apply
        </Button>
        <Button onClick={onDismiss}>Cancel</Button>
      </DrawerFooter>
    </OverlayDrawer>
  );
};
